import { Button } from "@dropbox/dig-components/buttons";
import { UIIcon } from "@dropbox/dig-icons";
import { ChevronLeftLine } from "@dropbox/dig-icons/assets";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { DestinationSurface } from "@mirage/analytics/events/enums/destination_surface";
import { PAP_Click_BackToDestinationSurface } from "@mirage/analytics/events/types/click_back_to_destination_surface";
import type { ScenarioMeta } from "@mirage/service-conversation/utils/buildConversationRouterState";
import i18n from "@mirage/translations";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

type ScenarioBackButtonProps = {
  scenario: ScenarioMeta;
};

const DESTINATION_SURFACE_MAP: { [key: string]: DestinationSurface } = {
  feed: "start_page",
  search: "serp",
  stack: "stack_details_page",
  stack_item_summarize: "stack_details_page",
};

export const ScenarioBackButton = ({ scenario }: ScenarioBackButtonProps) => {
  const navigate = useNavigate();
  const { reportPapEvent } = useMirageAnalyticsContext();

  const buttonText = useMemo(() => {
    switch (scenario.type) {
      case "stack":
      case "stack_item_summarize":
        return i18n.t("back_to_stack");
      case "search":
        return i18n.t("back_to_search");
      case "feed":
        return i18n.t("back_to_home");
      default:
        return i18n.t("back");
    }
  }, [scenario.type]);

  const handleClick = () => {
    reportPapEvent(
      PAP_Click_BackToDestinationSurface({
        actionSurfaceComponent: "top_nav",
        dashActionSurface: "conversation_page",
        destinationSurface: DESTINATION_SURFACE_MAP[scenario.type],
        featureLine: "assist",
      }),
    );

    switch (scenario.type) {
      case "stack":
        navigate(`/stacks/${scenario.sharingId}`);
        break;
      case "stack_item_summarize":
        navigate(`/stacks/${scenario.stackSharingId}`);
        break;
      case "search":
        navigate(`/search_results${scenario.query}`);
        break;
      case "feed":
        navigate("/");
        break;
      case "other":
        navigate(-1);
        break;
      default:
        throw new Error("Invalid scenario type");
    }
  };

  return (
    <Button
      onClick={handleClick}
      withIconStart={<UIIcon src={ChevronLeftLine} />}
      size="medium"
      variant="opacity"
    >
      {buttonText}
    </Button>
  );
};
